import { useEffect, useState } from "react";
import { UrlApi } from "../../../../../../../services/apirest";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import MaterialReactTable from "material-react-table";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
  Chip,
} from "@mui/material";
import Navbar from "../../../../../../Navbar";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MaximoRedondeoModal from "../modificarMaximoDeRedondeo/MaximoRedondeoModal";
import { getData } from "../../../../../../../services/library";
import { useToast } from "../../../../../../../Hooks/UseToast";
import { logEventos } from "../../../../../../../services/services";

const RecibosPendientes = () => {
  const [recibos, setRecibos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openValorMaximoRedondeoModal, setOpenValorMaximoRedondeoModal] =
    useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    row: null,
    estadoId: null,
    label: "",
  });
  const { showToast, Toast } = useToast();
  const [valorMaximoDelRedondeoActual, setValorMaximoDelRedondeoActual] =
    useState(null);

  const isSuccess = confirmModal.estadoId === 357;
  const accion = isSuccess ? "aprobado" : "rechazado";

  const fetchRecibos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${UrlApi}/ctacte/recibos/pendientes/global`);
      const data = await res.json();
      setRecibos(data);
    } catch (err) {
      showToast("Error al cargar recibos", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecibos();
    getData(
      "/ctacte/recibos/maximos/redondeo",
      setValorMaximoDelRedondeoActual,
    );
  }, []);

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    pagination: {
      pageSize: 10,
      pageIndex: 0,
    },
    grouping: ["sisven_id"],
  };

  const formatFecha = (fecha) =>
    fecha ? new Date(fecha).toLocaleDateString("es-AR") : "-";

  const formatMoney = (value) =>
    value !== null && value !== undefined
      ? new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
        }).format(value)
      : "-";

  const openConfirmModal = (row, estadoId, label) => {
    setConfirmModal({
      open: true,
      row,
      estadoId,
      label,
    });
  };

  const closeConfirmModal = () => {
    if (actionLoadingId) return;
    setConfirmModal({ open: false, row: null, estadoId: null, label: "" });
  };

  const confirmAction = async () => {
    const { row, estadoId, label } = confirmModal;

    setActionLoadingId(row.id);

    try {
      await fetch(
        `${UrlApi}/ctacte/recibos/actualizarEstado/${row.id}/${estadoId}`,
        {
          method: "PUT",
        },
      );

      showToast(`Recibo ${accion} correctamente`, "success");
      setRecibos((prev) => prev.filter((r) => r.id !== row.id));
      logEventos(
        `El recibo con el ID: ${row.id} fue ${accion} con exito`,
        "Recibos pendientes",
        `Recibo ${accion}`,
        localStorage.getItem("user"),
      );
      closeConfirmModal();
    } catch (err) {
      showToast(`Error al ${label}`, "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const columns = [
    {
      accessorKey: "cuit",
      header: "CUIT",
    },

    {
      accessorKey: "sisven_id",
      header: "Sisven ID",
    },
    {
      accessorKey: "fecha_de_pago",
      header: "Fecha pago",
      Cell: ({ cell }) => formatFecha(cell.getValue()),
    },
    {
      accessorKey: "created_at",
      header: "Fecha contable",
      Cell: ({ cell }) => formatFecha(cell.getValue()),
    },
    {
      id: "comprobante",
      header: "Comprobante",
      accessorFn: (row) =>
        `${row.pto_vta?.toString().padStart(4, "0")}-${row.numero_recibo
          ?.toString()
          .padStart(8, "0")}`,
    },
    {
      accessorKey: "user",
      header: "Usuario",
    },
    {
      accessorKey: "redondeo",
      header: "Redondeo",
      Cell: ({ cell }) => formatMoney(cell.getValue()),
    },
    {
      accessorKey: "total",
      header: "Total",
      Cell: ({ cell }) => formatMoney(cell.getValue()),
    },
  ];

  return (
    <>
      <Navbar />

      <Box
        sx={{
          p: 4,
          backgroundColor: "#f9fafb",
          borderRadius: "16px",
          boxShadow: 2,
        }}
      >
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Recibos Pendientes
            </Typography>

            <Typography color="text.secondary" fontSize={14}>
              Gestioná y aprobá los recibos pendientes del sistema
            </Typography>

            <Chip
              label={`${recibos.length} pendientes`}
              color="warning"
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>

          <Button
            variant="outlined"
            onClick={() => setOpenValorMaximoRedondeoModal(true)}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Modificar máximo redondeo
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </Box>
        ) : (
          <MaterialReactTable
            columns={columns}
            data={recibos ?? []}
            enableGlobalFilter
            enableColumnFilters
            enableGrouping
            enableRowActions
            positionActionsColumn="last"
            localization={MRT_Localization_ES}
            initialState={initialState}
            renderRowActions={({ row }) => {
              const isLoading = actionLoadingId === row.original.id;

              return (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Aprobar">
                    <span>
                      <IconButton
                        color="success"
                        disabled={isLoading}
                        onClick={() =>
                          openConfirmModal(row.original, 357, "aprobar")
                        }
                      >
                        {isLoading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <CheckIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Rechazar">
                    <span>
                      <IconButton
                        color="error"
                        disabled={isLoading}
                        onClick={() =>
                          openConfirmModal(row.original, 359, "rechazar")
                        }
                      >
                        <CloseIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            }}
            muiTableProps={{
              sx: {
                borderRadius: "12px",
                "& .MuiTableRow-root:hover": {
                  backgroundColor: "#f1f5f9",
                },
              },
            }}
          />
        )}

        {Toast}
        <Dialog
          open={confirmModal.open}
          onClose={closeConfirmModal}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: "20px",
              p: 0,
              overflow: "hidden",
            },
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 3,
              py: 2,
              background: isSuccess
                ? "linear-gradient(135deg, #16a34a, #22c55e)"
                : "linear-gradient(135deg, #dc2626, #ef4444)",
              color: "white",
            }}
          >
            {isSuccess ? <CheckCircleIcon /> : <WarningAmberIcon />}
            <Typography fontWeight="bold" fontSize="18px">
              Confirmar acción
            </Typography>
          </Box>

          {/* CONTENT */}
          <DialogContent sx={{ px: 3, py: 3 }}>
            <Typography sx={{ mb: 2, fontSize: "15px" }}>
              ¿Seguro que querés{" "}
              <b style={{ textTransform: "uppercase" }}>{confirmModal.label}</b>{" "}
              este recibo?
            </Typography>

            {confirmModal.row && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography color="text.secondary">Comprobante</Typography>
                    <Typography fontWeight={600}>
                      {`${confirmModal.row.pto_vta
                        ?.toString()
                        .padStart(4, "0")}-${confirmModal.row.numero_recibo
                        ?.toString()
                        .padStart(8, "0")}`}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography color="text.secondary">CUIT</Typography>
                    <Typography fontWeight={500}>
                      {confirmModal.row.cuit}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box display="flex" justifyContent="space-between">
                    <Typography fontWeight="bold">Total</Typography>
                    <Typography fontWeight="bold" color="primary">
                      {formatMoney(confirmModal.row.total)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
          </DialogContent>

          {/* ACTIONS */}
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              gap: 1,
            }}
          >
            <Button
              onClick={closeConfirmModal}
              disabled={actionLoadingId}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              color={isSuccess ? "success" : "error"}
              onClick={confirmAction}
              disabled={actionLoadingId}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                minWidth: "120px",
                fontWeight: "bold",
              }}
            >
              {actionLoadingId ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                confirmModal.label
              )}
            </Button>
          </DialogActions>
        </Dialog>
        <MaximoRedondeoModal
          onClose={() => setOpenValorMaximoRedondeoModal(false)}
          open={openValorMaximoRedondeoModal}
          valorRedondeoActual={valorMaximoDelRedondeoActual}
          showToast={showToast}
        />
      </Box>
    </>
  );
};

export default RecibosPendientes;
